import { Trans, useLingui } from "@lingui/react/macro";
import type { AdminConversationRequestUsage } from "../api.js";

function formatTokens(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatRecordedUsd(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 8,
  }).format(value);
}

export function RequestPricing({ request }: { request: AdminConversationRequestUsage }) {
  const { t, i18n } = useLingui();
  const unavailable = t`Unavailable`;
  const rate = (value: number) => new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 8 }).format(value);
  const cacheRate = (value: number | null) => value === null
    ? t`${rate(request.rates.inputPerMtok)} (input-rate fallback)`
    : rate(value);
  return (
    <article className="request-pricing">
      <div className="request-pricing-heading">
        <strong><Trans>Request</Trans></strong>
        <span className="mono">{request.providerRequestId ?? request.id}</span>
        <strong className="mono">{formatRecordedUsd(request.costUsd, i18n.locale)}</strong>
      </div>
      <div className="muted small">{request.provider} · {request.modelId} · {new Date(request.createdAt).toLocaleString(i18n.locale)}</div>
      <div className="kv">
        <div><strong><Trans>Applied tier</Trans></strong></div>
        <div>{request.appliedPricingTier === null ? unavailable : request.appliedPricingTier === "standard" ? t`Standard` : t`Long context`}</div>
        <div><strong><Trans>Input threshold</Trans></strong></div>
        <div>{request.inputTokenThresholdSnapshot === null
          ? request.appliedPricingTier === null ? unavailable : t`No threshold recorded`
          : formatTokens(request.inputTokenThresholdSnapshot, i18n.locale)}</div>
      </div>
      <div className="request-pricing-table">
        <table>
          <thead><tr><th><Trans>Token usage</Trans></th><th><Trans>Tokens</Trans></th><th><Trans>Recorded rate (USD / million tokens)</Trans></th></tr></thead>
          <tbody>
            <tr><td><Trans>Input tokens</Trans></td><td>{formatTokens(request.tokens.inputTokens, i18n.locale)}</td><td>{rate(request.rates.inputPerMtok)}</td></tr>
            <tr><td><Trans>Cached read</Trans></td><td>{formatTokens(request.tokens.cachedInputTokens, i18n.locale)}</td><td>{cacheRate(request.rates.cachedPerMtok)}</td></tr>
            <tr><td><Trans>Cache write</Trans></td><td>{formatTokens(request.tokens.cacheWriteTokens, i18n.locale)}</td><td>{cacheRate(request.rates.cacheWritePerMtok)}</td></tr>
            <tr><td><Trans>Output tokens</Trans></td><td>{formatTokens(request.tokens.outputTokens, i18n.locale)}</td><td>{rate(request.rates.outputPerMtok)}</td></tr>
          </tbody>
        </table>
      </div>
      <p className="muted small"><Trans>Omitted cache rates use this request's selected input rate. Explicit zero rates remain zero. Historical costs are displayed as recorded.</Trans></p>
    </article>
  );
}
